'use client'

import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  getBiometricKind,
  hasStoredBiometricCredentials,
  readBiometricCredentials,
  type BiometricKind,
} from '@/utils/nativeBiometric'

type UseBiometricLoginOptions = {
  onCredentials: (identifier: string, pin: string) => Promise<void>
}

export function useBiometricLogin({ onCredentials }: UseBiometricLoginOptions) {
  const { t } = useTranslation()
  const [kind, setKind] = useState<BiometricKind | null>(null)
  const [ready, setReady] = useState(false)
  const [loading, setLoading] = useState(false)

  const refresh = useCallback(async () => {
    const biometricKind = await getBiometricKind()
    if (!biometricKind) {
      setKind(null)
      setReady(false)
      return
    }
    const stored = await hasStoredBiometricCredentials()
    setKind(biometricKind)
    setReady(stored)
  }, [])

  useEffect(() => {
    void refresh()
    const onVisible = () => {
      if (document.visibilityState === 'visible') void refresh()
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => document.removeEventListener('visibilitychange', onVisible)
  }, [refresh])

  const label =
    kind === 'face'
      ? t('auth.signInWithFaceId', { defaultValue: 'Sign in with Face ID' })
      : kind === 'touch'
        ? t('auth.signInWithTouchId', { defaultValue: 'Sign in with Touch ID' })
        : t('auth.signInWithBiometrics', { defaultValue: 'Sign in with biometrics' })

  const loginWithBiometric = useCallback(async () => {
    if (loading) return
    setLoading(true)
    try {
      const credentials = await readBiometricCredentials()
      if (!credentials) return
      await onCredentials(credentials.identifier, credentials.pin)
    } catch {
      // User cancelled or verification failed — stay on login form.
    } finally {
      setLoading(false)
    }
  }, [loading, onCredentials])

  return {
    biometricAvailable: Boolean(kind),
    biometricReady: ready,
    biometricLabel: label,
    biometricLoading: loading,
    loginWithBiometric,
    refreshBiometric: refresh,
  }
}
